// ============================================================================
// BP-13-02: Admin Store
// BrightPath Platform - Admin Frontend
// ============================================================================

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  TimePeriod,
  AdminFiltersState,
  UserRole,
  UserStatus,
  ModerationItemType,
  ModerationStatus,
  ResourceStatus,
  ResourceType,
} from '@/types/admin';

// =============================================================================
// Types
// =============================================================================

interface UserFilters extends AdminFiltersState {
  role: UserRole | null;
  status: UserStatus | null;
  emailVerified: boolean | null;
}

interface ModerationFilters {
  itemType: ModerationItemType | null;
  status: ModerationStatus | null;
  priority: 'low' | 'medium' | 'high' | 'urgent' | null;
  sortBy: 'createdAt' | 'flagCount' | 'priority';
  sortOrder: 'asc' | 'desc';
}

interface ResourceFilters extends AdminFiltersState {
  categoryId: string | null;
  resourceType: ResourceType | null;
  status: ResourceStatus | null;
}

interface AnalyticsSettings {
  period: TimePeriod;
  startDate: string | null;
  endDate: string | null;
  compareWithPrevious: boolean;
}

interface AdminUIState {
  sidebarCollapsed: boolean;
  selectedItems: string[];
  activeTab: string;
  showFilters: boolean;
}

interface AdminState {
  // User Management
  userFilters: UserFilters;
  setUserFilters: (filters: Partial<UserFilters>) => void;
  resetUserFilters: () => void;

  // Moderation
  moderationFilters: ModerationFilters;
  setModerationFilters: (filters: Partial<ModerationFilters>) => void;
  resetModerationFilters: () => void;

  // Resources
  resourceFilters: ResourceFilters;
  setResourceFilters: (filters: Partial<ResourceFilters>) => void;
  resetResourceFilters: () => void;

  // Analytics
  analyticsSettings: AnalyticsSettings;
  setAnalyticsSettings: (settings: Partial<AnalyticsSettings>) => void;

  // UI State
  ui: AdminUIState;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSelectedItems: (items: string[]) => void;
  toggleSelectedItem: (itemId: string) => void;
  clearSelectedItems: () => void;
  setActiveTab: (tab: string) => void;
  setShowFilters: (show: boolean) => void;

  // Recent Activity
  recentSearches: string[];
  addRecentSearch: (search: string) => void;
  clearRecentSearches: () => void;

  // Preferences
  preferences: {
    defaultPageSize: number;
    autoRefresh: boolean;
    darkMode: boolean;
    compactView: boolean;
  };
  setPreferences: (prefs: Partial<AdminState['preferences']>) => void;
}

// =============================================================================
// Default Values
// =============================================================================

const defaultUserFilters: UserFilters = {
  search: '',
  dateFrom: null,
  dateTo: null,
  status: null,
  sortBy: 'createdAt',
  sortOrder: 'desc',
  role: null,
  emailVerified: null,
};

const defaultModerationFilters: ModerationFilters = {
  itemType: null,
  status: null,
  priority: null,
  sortBy: 'createdAt',
  sortOrder: 'desc',
};

const defaultResourceFilters: ResourceFilters = {
  search: '',
  dateFrom: null,
  dateTo: null,
  status: null,
  sortBy: 'updatedAt',
  sortOrder: 'desc',
  categoryId: null,
  resourceType: null,
};

const defaultAnalyticsSettings: AnalyticsSettings = {
  period: 'last_30_days',
  startDate: null,
  endDate: null,
  compareWithPrevious: false,
};

const defaultUIState: AdminUIState = {
  sidebarCollapsed: false,
  selectedItems: [],
  activeTab: 'overview',
  showFilters: false,
};

// =============================================================================
// Store
// =============================================================================

export const useAdminStore = create<AdminState>()(
  devtools(
    persist(
      (set) => ({
        // User Management
        userFilters: defaultUserFilters,
        setUserFilters: (filters) =>
          set((state) => ({
            userFilters: { ...state.userFilters, ...filters },
          })),
        resetUserFilters: () => set({ userFilters: defaultUserFilters }),

        // Moderation
        moderationFilters: defaultModerationFilters,
        setModerationFilters: (filters) =>
          set((state) => ({
            moderationFilters: { ...state.moderationFilters, ...filters },
          })),
        resetModerationFilters: () =>
          set({ moderationFilters: defaultModerationFilters }),

        // Resources
        resourceFilters: defaultResourceFilters,
        setResourceFilters: (filters) =>
          set((state) => ({
            resourceFilters: { ...state.resourceFilters, ...filters },
          })),
        resetResourceFilters: () => set({ resourceFilters: defaultResourceFilters }),

        // Analytics
        analyticsSettings: defaultAnalyticsSettings,
        setAnalyticsSettings: (settings) =>
          set((state) => ({
            analyticsSettings: { ...state.analyticsSettings, ...settings },
          })),

        // UI State
        ui: defaultUIState,
        toggleSidebar: () =>
          set((state) => ({
            ui: { ...state.ui, sidebarCollapsed: !state.ui.sidebarCollapsed },
          })),
        setSidebarCollapsed: (collapsed) =>
          set((state) => ({
            ui: { ...state.ui, sidebarCollapsed: collapsed },
          })),
        setSelectedItems: (items) =>
          set((state) => ({
            ui: { ...state.ui, selectedItems: items },
          })),
        toggleSelectedItem: (itemId) =>
          set((state) => {
            const items = state.ui.selectedItems.includes(itemId)
              ? state.ui.selectedItems.filter((id) => id !== itemId)
              : [...state.ui.selectedItems, itemId];
            return { ui: { ...state.ui, selectedItems: items } };
          }),
        clearSelectedItems: () =>
          set((state) => ({
            ui: { ...state.ui, selectedItems: [] },
          })),
        setActiveTab: (tab) =>
          set((state) => ({
            ui: { ...state.ui, activeTab: tab },
          })),
        setShowFilters: (show) =>
          set((state) => ({
            ui: { ...state.ui, showFilters: show },
          })),

        // Recent Activity
        recentSearches: [],
        addRecentSearch: (search) =>
          set((state) => {
            const searches = [
              search,
              ...state.recentSearches.filter((s) => s !== search),
            ].slice(0, 10);
            return { recentSearches: searches };
          }),
        clearRecentSearches: () => set({ recentSearches: [] }),

        // Preferences
        preferences: {
          defaultPageSize: 20,
          autoRefresh: true,
          darkMode: false,
          compactView: false,
        },
        setPreferences: (prefs) =>
          set((state) => ({
            preferences: { ...state.preferences, ...prefs },
          })),
      }),
      {
        name: 'brightpath-admin-store',
        partialize: (state) => ({
          // Only persist preferences and UI state
          preferences: state.preferences,
          ui: {
            sidebarCollapsed: state.ui.sidebarCollapsed,
          },
          recentSearches: state.recentSearches,
        }),
      }
    ),
    { name: 'AdminStore' }
  )
);

// =============================================================================
// Selectors
// =============================================================================

export const selectUserFilters = (state: AdminState) => state.userFilters;
export const selectModerationFilters = (state: AdminState) => state.moderationFilters;
export const selectResourceFilters = (state: AdminState) => state.resourceFilters;
export const selectAnalyticsSettings = (state: AdminState) => state.analyticsSettings;
export const selectUI = (state: AdminState) => state.ui;
export const selectPreferences = (state: AdminState) => state.preferences;
export const selectSelectedItems = (state: AdminState) => state.ui.selectedItems;
export const selectSidebarCollapsed = (state: AdminState) => state.ui.sidebarCollapsed;
